/* istanbul ignore file */
import React, { useEffect, useState } from 'react';
import { Box } from '@interstate/components/Box';
import { useDispatch, useSelector } from 'react-redux';
import * as proposalActionCreators from '@makemydeal/dr-dash-store/actions/proposalActionCreators';
import * as proposalSelectors from '@makemydeal/dr-dash-store/selectors/proposalSelectors';
import { Table } from '@interstate/components/Table/Table';
import { Typography } from '@interstate/components/Typography';
import { Action } from '@interstate/components/Action';
import { Tooltip } from '@interstate/components/Tooltip';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import type { ProposalMetadata } from '@makemydeal/dr-dash-types';
import * as printPdfDealSelectors from '@makemydeal/dr-dash-store/selectors/printPdfDealSelectors';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import Actions from './columns/Actions';
import ProposalChip from './columns/ProposalChip';
import { ProposalRemoveDrawer } from './ProposalRemoveDrawer.interstate';
import ProposalListAlert from './ProposalListAlertAndLoading';
import GlobalPrintingSettings from '../globalSettings/GlobalPrintingSettings';
import {
    createSelectingProposalForEditingEvent,
    createCopyPrintProposalEvent
} from '../../dealTemplates/analytics/cdlEventCreators';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';

const isFourSquareProposal = (proposal: ProposalMetadata) => proposal.type === 'fourSquare';

const ProposalList: React.FC<{
    editProposal: (id: string, name: string) => void;
}> = ({ editProposal }) => {
    const dispatch = useDispatch();
    const theme = useInterstateTheme();
    const printingProposals = useSelector(proposalSelectors.getProposalList);
    const isListLoading = useSelector(proposalSelectors.getProposalsLoading);
    const [proposalToRemove, setProposalToRemove] = useState<string | null>(null);
    const appliedTemplateId = useSelector(dealTemplateSelectors.getAppliedDealTemplateId);
    const isMvLiteEnabled = useSelector(dealerSelectors.isMvLiteEnabled);
    const isBalloonOrFlexBuy = useSelector(printPdfDealSelectors.getAreAllScopesBalloonOrFlexBuy);

    const onDeleteProposal = (id: string) => {
        dispatch(proposalActionCreators.deleteProposal(id));
        setProposalToRemove(null);
    };
    const onCopyProposal = (id: string) => {
        dispatch(createCopyPrintProposalEvent({ proposalId: id }, isMvLiteEnabled));
        dispatch(proposalActionCreators.copyProposal(id));
    };
    const onSetPrimaryProposal = (id: string) => {
        dispatch(proposalActionCreators.setIsPrimaryProposal(id));
    };

    useEffect(() => {
        dispatch(proposalActionCreators.getProposalList());
    }, []);

    const columns = [
        {
            dataIndex: 'title',
            title: 'Printing Proposal',
            render: (value: string, record: ProposalMetadata) => {
                const isFourSquare = isFourSquareProposal(record);
                const isDisabledByBalloonOrFlexBuy = isFourSquare && isBalloonOrFlexBuy;
                const isDefaultStandard = record.isDefault && !isFourSquare;

                const handleClick = () => {
                    editProposal(record.id, record.title);
                    dispatch(
                        createSelectingProposalForEditingEvent(
                            { proposalId: record.id, templateId: appliedTemplateId },
                            isMvLiteEnabled
                        )
                    );
                };

                const actionElement = (
                    <Action
                        data-testid={`proposal-title-action-${record.id}`}
                        size="md"
                        disabled={isDefaultStandard || isDisabledByBalloonOrFlexBuy}
                        onClick={handleClick}
                    >
                        <Typography
                            variant="body-md"
                            tag="span"
                            sx={{ textAlign: 'left' }}
                            color={
                                isDefaultStandard || isDisabledByBalloonOrFlexBuy ? 'base.color.gray.500' : 'base.color.blue.700'
                            }
                        >
                            {value}
                        </Typography>
                    </Action>
                );

                if (isDisabledByBalloonOrFlexBuy) {
                    return (
                        <Box display="flex" alignItems="center" gap="8px">
                            {actionElement}
                            <Tooltip
                                data-testid={`proposal-balloon-flexbuy-tooltip-${record.id}`}
                                position="top-start"
                                size="large"
                                toolTipContent="This type of printing proposal is not available for Balloon and Flex Buy deals."
                                sx={{
                                    fontSize: `${theme.tokens.ComponentTooltipFontSizeLarge} !important`,
                                    lineHeight: `${theme.tokens.ComponentTooltipFontLineHeightLarge} !important`,
                                    backgroundColor: `${theme.tokens.ComponentTooltipBgColor} !important`
                                }}
                            >
                                <InformationCircleIcon
                                    color={theme.tokens.BaseColorBlue700}
                                    style={{
                                        transform: 'translateY(0.15rem)'
                                    }}
                                    fontSize="large"
                                    data-testid={`proposal-balloon-flexbuy-info-icon-${record.id}`}
                                />
                            </Tooltip>
                        </Box>
                    );
                }

                return actionElement;
            },
            width: '60%'
        },
        {
            dataIndex: 'chip',
            title: '',
            render: (_value: boolean, record: ProposalMetadata) => <ProposalChip record={record} />,
            width: '30%'
        },
        {
            dataIndex: 'actions',
            title: '',
            render: (_value: boolean, record: ProposalMetadata) => (
                <Actions
                    record={record}
                    setProposalToRemove={setProposalToRemove}
                    onCopyProposal={onCopyProposal}
                    onSetPrimaryProposal={onSetPrimaryProposal}
                    isBalloonOrFlexBuy={isBalloonOrFlexBuy}
                />
            )
        }
    ];

    return (
        <>
            <ProposalListAlert />
            <Box height="100%" data-testid="proposal-slideout-list">
                <GlobalPrintingSettings />
                <Table columns={columns} data={printingProposals} enablePagination={false} isLoading={isListLoading} />
            </Box>
            {!!proposalToRemove && (
                <ProposalRemoveDrawer
                    hideHandler={() => setProposalToRemove(null)}
                    removeHandler={() => onDeleteProposal(proposalToRemove)}
                />
            )}
        </>
    );
};

export default ProposalList;
